/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/components/shared/ConfirmDialog.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/platform/design-system/components/shared/ConfirmDialog.tsx
 * filename: ConfirmDialog.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

/**
 * ConfirmDialog Component
 *
 * A modal confirmation dialog (Radix Dialog) that replaces native window.confirm() calls
 * with a styled, accessible dialog. Supports danger/warning/info variants.
 *
 * Usage:
 *   <ConfirmDialog
 *     open={isOpen}
 *     title="Delete Item"
 *     message="Are you sure? This cannot be undone."
 *     variant="danger"
 *     confirmLabel="Delete"
 *     onConfirm={handleDelete}
 *     onCancel={() => setIsOpen(false)}
 *   />
 *
 * Or use the useConfirmDialog hook for a Promise-based API.
 */

import { useRef, useCallback, type ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useReturnFocusOnClose } from '@/platform/design-system/components/ui/returnFocus';
import { clsx } from 'clsx';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';

export type ConfirmDialogVariant = 'danger' | 'warning' | 'info';

export interface ConfirmDialogProps {
  /** Whether the dialog is visible */
  open: boolean;
  /** Dialog title */
  title: string;
  /** Dialog message / description */
  message: ReactNode;
  /** Visual variant controlling confirm button color and icon */
  variant?: ConfirmDialogVariant;
  /** Label for the confirm button (default: "Confirm") */
  confirmLabel?: string;
  /** Label for the cancel button (default: "Cancel") */
  cancelLabel?: string;
  /** Whether the confirm action is in progress. */
  isConfirming?: boolean;
  /** Disables the confirm button (e.g. until a typed confirmation matches). */
  confirmDisabled?: boolean;
  /** Extra content under the message (a typed-confirmation or reason field). */
  children?: ReactNode;
  /** Called when the user confirms */
  onConfirm: () => void;
  /** Called when the user cancels (backdrop click, Escape, or Cancel button) */
  onCancel: () => void;
}

const variantConfig = {
  danger: {
    icon: ShieldExclamationIcon,
    iconBg: 'bg-red-500/14',
    iconColor: 'text-red-400',
    confirmBtn:
      'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    iconBg: 'bg-amber-500/14',
    iconColor: 'text-amber-400',
    confirmBtn:
      'bg-amber-600 text-white hover:bg-amber-700 focus-visible:ring-amber-500',
  },
  info: {
    icon: InformationCircleIcon,
    iconBg: 'bg-accent/14',
    iconColor: 'text-blue-400',
    confirmBtn:
      'bg-accent text-white hover:bg-accent-hover focus-visible:ring-accent-ring',
  },
};

export function ConfirmDialog({
  open,
  title,
  message,
  variant = 'danger',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isConfirming = false,
  confirmDisabled = false,
  children,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const config = variantConfig[variant];
  const Icon = config.icon;
  // Built on Radix Dialog so it nests correctly inside another open
  // Dialog/Drawer/ModalDialog (focus scope and outside-pointer layering),
  // and so focus returns to the opener when it closes.
  const returnFocus = useReturnFocusOnClose(
    useCallback((event: Event) => {
      // Safer default: start on Cancel, or on a field the caller marked.
      event.preventDefault();
      const field = dialogRef.current?.querySelector<HTMLElement>('[data-autofocus]');
      (field ?? cancelRef.current)?.focus();
    }, [])
  );

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !isConfirming) onCancel();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay asChild>
          <button
            type="button"
            tabIndex={-1}
            aria-label="Dismiss dialog"
            disabled={isConfirming}
            className="fixed inset-0 z-50 bg-[rgb(2_6_23/0.58)] backdrop-blur-[2px] animate-in fade-in-0 duration-200"
            onClick={onCancel}
          />
        </DialogPrimitive.Overlay>
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center">
          <DialogPrimitive.Content
            ref={dialogRef}
            {...returnFocus}
            onEscapeKeyDown={(event) => {
              if (isConfirming) event.preventDefault();
            }}
            // The backdrop button handles outside clicks itself.
            onInteractOutside={(event) => event.preventDefault()}
            className={clsx(
              'shell-frost pointer-events-auto relative z-10 mx-4 w-full max-w-md focus:outline-none',
              'border border-[rgb(var(--color-border-primary)/0.82)]',
              'rounded-[1.5rem] shadow-[0_30px_72px_-36px_rgb(15_23_42/0.52)] p-6',
              'animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2 duration-200'
            )}
          >
            <div className="flex gap-4">
              <div
                className={clsx(
                  'shrink-0 flex items-center justify-center w-10 h-10 rounded-full',
                  config.iconBg
                )}
              >
                <Icon className={clsx('h-5 w-5', config.iconColor)} />
              </div>

              <div className="flex-1 min-w-0">
                <DialogPrimitive.Title asChild>
                  <h3 className="text-base font-semibold text-[rgb(var(--color-text-primary))]">
                    {title}
                  </h3>
                </DialogPrimitive.Title>
                <DialogPrimitive.Description asChild>
                  <div className="mt-1 text-sm text-[rgb(var(--color-text-secondary))] leading-relaxed">
                    {message}
                  </div>
                </DialogPrimitive.Description>
                {children && <div className="mt-3">{children}</div>}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                ref={cancelRef}
                type="button"
                onClick={onCancel}
                disabled={isConfirming}
                className={clsx(
                  'control-action rounded-xl px-4 py-2 text-sm font-medium',
                  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-ring focus-visible:ring-offset-2'
                )}
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={isConfirming || confirmDisabled}
                aria-busy={isConfirming}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-xl transition-colors shadow-[0_18px_34px_-24px_rgb(15_23_42/0.35)]',
                  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2',
                  config.confirmBtn
                )}
              >
                {isConfirming ? 'Working…' : confirmLabel}
              </button>
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
